// AGC: Farm Machine Types for DroidFarm
// AGC: Standard Brazilian farm equipment list

/**
 * AGC: Machine category type
 */
export type MachineCategory = 
  | 'trator'
  | 'colheitadeira'
  | 'plantadeira'
  | 'pulverizador'
  | 'distribuidor'
  | 'implemento'
  | 'irrigacao'
  | 'transporte'
  | 'outros'

/**
 * AGC: Farm machine interface
 */
export interface FarmMachine {
  id: string
  name: string
  category: MachineCategory
  icon: string
  brand?: string
  model?: string
}

/**
 * AGC: Machine brands by category
 * AGC: Common brands for each equipment type in Brazilian agriculture
 */
export const MACHINE_BRANDS: Record<MachineCategory, string[]> = {
  trator: [
    'John Deere',
    'Case IH',
    'New Holland',
    'Massey Ferguson',
    'Valtra',
    'Fendt',
    'Kubota',
    'Mahindra',
    'Agrale',
  ],
  colheitadeira: [
    'John Deere',
    'Case IH',
    'New Holland',
    'Massey Ferguson',
    'CLAAS',
    'Ideal',
  ],
  pulverizador: [
    'John Deere',
    'Case IH',
    'New Holland',
    'Massey Ferguson',
    'Stara',
    'Jacto',
    'Montana',
    'PLA',
    'Uniport',
  ],
  plantadeira: [
    'John Deere',
    'Case IH',
    'New Holland',
    'Massey Ferguson',
    'Stara',
    'Tatu Marchesan',
    'Jumil',
    'Baldan',
    'Semeato',
    'Vence Tudo',
    'Kuhn',
    'Piccin',
    'Imasa',
    'Civemasa',
    'KF Implementos',
  ],
  distribuidor: [
    'John Deere',
    'Case IH',
    'New Holland',
    'Massey Ferguson',
    'Stara',
    'Jacto',
    'Baldan',
    'Tatu Marchesan',
    'Jumil',
    'Semeato',
    'Vence Tudo',
    'Piccin',
    'Kuhn',
  ],
  implemento: [
    'Baldan',
    'Tatu Marchesan',
    'Stara',
    'Jacto',
    'Piccin',
    'Kuhn',
    'Jan',
    'Ikeda',
    'Santa Izabel',
  ],
  irrigacao: [
    'Valley',
    'Lindsay',
    'Reinke',
    'Fockink',
    'Krebs',
    'Carborundum',
  ],
  transporte: [
    'Mercedes-Benz',
    'Volvo',
    'Scania',
    'Volkswagen',
    'Ford',
    'Iveco',
    'Randazzo',
    'Guerra',
  ],
  outros: [
    'John Deere',
    'Case IH',
    'New Holland',
    'Massey Ferguson',
    'Jacto',
    'Stara',
  ],
}

/**
 * AGC: Standard farm machines list
 * AGC: Common equipment found on Brazilian farms
 */
export const FARM_MACHINES: FarmMachine[] = [
  // AGC: Tratores (Tractors)
  { id: 'trator-pequeno', name: 'Trator Pequeno (até 75cv)', category: 'trator', icon: '🚜' },
  { id: 'trator-medio', name: 'Trator Médio (75-140cv)', category: 'trator', icon: '🚜' },
  { id: 'trator-grande', name: 'Trator Grande (140-220cv)', category: 'trator', icon: '🚜' },
  { id: 'trator-articulado', name: 'Trator Articulado (220cv+)', category: 'trator', icon: '🚜' },
  { id: 'trator-esteira', name: 'Trator de Esteira', category: 'trator', icon: '🚜' },
  
  // AGC: Colheitadeiras (Harvesters)
  { id: 'colheitadeira-graos', name: 'Colheitadeira de Grãos', category: 'colheitadeira', icon: '🌾' },
  { id: 'colheitadeira-cana', name: 'Colheitadeira de Cana', category: 'colheitadeira', icon: '🌿' },
  { id: 'colheitadeira-cafe', name: 'Colheitadeira de Café', category: 'colheitadeira', icon: '☕' },
  { id: 'colheitadeira-algodao', name: 'Colheitadeira de Algodão', category: 'colheitadeira', icon: '☁️' },
  { id: 'colheitadeira-forragem', name: 'Colheitadeira de Forragem', category: 'colheitadeira', icon: '🌱' },
  
  // AGC: Plantadeiras (Planters)
  { id: 'plantadeira-4-linhas', name: 'Plantadeira 4-8 Linhas', category: 'plantadeira', icon: '🌱' },
  { id: 'plantadeira-12-linhas', name: 'Plantadeira 12-16 Linhas', category: 'plantadeira', icon: '🌱' },
  { id: 'plantadeira-24-linhas', name: 'Plantadeira 24+ Linhas', category: 'plantadeira', icon: '🌱' },
  { id: 'plantadeira-pneumatica', name: 'Plantadeira Pneumática', category: 'plantadeira', icon: '🌱' },
  { id: 'semeadora-lanço', name: 'Semeadora a Lanço', category: 'plantadeira', icon: '🌾' },
  
  // AGC: Pulverizadores (Sprayers)
  { id: 'pulverizador-barras', name: 'Pulverizador de Barras', category: 'pulverizador', icon: '💧' },
  { id: 'pulverizador-autopropelido', name: 'Pulverizador Autopropelido', category: 'pulverizador', icon: '💧' },
  { id: 'pulverizador-costal', name: 'Pulverizador Costal', category: 'pulverizador', icon: '💧' },
  { id: 'pulverizador-drone', name: 'Drone Pulverizador', category: 'pulverizador', icon: '🚁' },
  { id: 'atomizador', name: 'Atomizador/Turbopulverizador', category: 'pulverizador', icon: '💨' },
  
  // AGC: Distribuidores / Adubadeiras (Spreaders)
  { id: 'distribuidor-calcario', name: 'Distribuidor de Calcário', category: 'distribuidor', icon: '🌾' },
  { id: 'distribuidor-adubo', name: 'Distribuidor de Adubo', category: 'distribuidor', icon: '🌾' },
  { id: 'adubadeira-linha', name: 'Adubadeira em Linha', category: 'distribuidor', icon: '🌾' },
  { id: 'adubadeira-cobertura', name: 'Adubadeira de Cobertura', category: 'distribuidor', icon: '🌾' },
  { id: 'distribuidor-sementes', name: 'Distribuidor de Sementes', category: 'distribuidor', icon: '🌱' },
  { id: 'espalhador-esterco', name: 'Espalhador de Esterco', category: 'distribuidor', icon: '🌾' },
  { id: 'distribuidor-vinhaça', name: 'Distribuidor de Vinhaça', category: 'distribuidor', icon: '💧' },
  { id: 'calcariadora', name: 'Calcariadora', category: 'distribuidor', icon: '🌾' },
  
  // AGC: Implementos (Implements)
  { id: 'arado-discos', name: 'Arado de Discos', category: 'implemento', icon: '⚙️' },
  { id: 'arado-aivecas', name: 'Arado de Aivecas', category: 'implemento', icon: '⚙️' },
  { id: 'grade-aradora', name: 'Grade Aradora', category: 'implemento', icon: '⚙️' },
  { id: 'grade-niveladora', name: 'Grade Niveladora', category: 'implemento', icon: '⚙️' },
  { id: 'subsolador', name: 'Subsolador', category: 'implemento', icon: '⚙️' },
  { id: 'escarificador', name: 'Escarificador', category: 'implemento', icon: '⚙️' },
  { id: 'rolo-faca', name: 'Rolo-Faca', category: 'implemento', icon: '⚙️' },
  { id: 'roçadora', name: 'Roçadora/Roçadeira', category: 'implemento', icon: '🌿' },
  { id: 'cultivador', name: 'Cultivador', category: 'implemento', icon: '⚙️' },
  { id: 'sulcador', name: 'Sulcador', category: 'implemento', icon: '⚙️' },
  
  // AGC: Irrigação (Irrigation)
  { id: 'pivo-central', name: 'Pivô Central', category: 'irrigacao', icon: '💦' },
  { id: 'carretel-irrigacao', name: 'Carretel de Irrigação', category: 'irrigacao', icon: '💦' },
  { id: 'gotejamento', name: 'Sistema Gotejamento', category: 'irrigacao', icon: '💧' },
  { id: 'aspersao', name: 'Sistema Aspersão', category: 'irrigacao', icon: '💦' },
  { id: 'motobomba', name: 'Motobomba', category: 'irrigacao', icon: '⚡' },
  
  // AGC: Transporte (Transport)
  { id: 'caminhao-graneleiro', name: 'Caminhão Graneleiro', category: 'transporte', icon: '🚛' },
  { id: 'caminhao-carga', name: 'Caminhão de Carga', category: 'transporte', icon: '🚚' },
  { id: 'carreta-agricola', name: 'Carreta Agrícola', category: 'transporte', icon: '🚛' },
  { id: 'transbordo', name: 'Transbordo', category: 'transporte', icon: '🚜' },
  { id: 'tanque-combustivel', name: 'Tanque de Combustível', category: 'transporte', icon: '⛽' },
  { id: 'tanque-agua', name: 'Tanque de Água', category: 'transporte', icon: '💧' },
  
  // AGC: Outros (Others)
  { id: 'enfardadora', name: 'Enfardadora', category: 'outros', icon: '📦' },
  { id: 'ensiladeira', name: 'Ensiladeira', category: 'outros', icon: '🌿' },
  { id: 'misturador-racao', name: 'Misturador de Ração', category: 'outros', icon: '🥣' },
  { id: 'secador-graos', name: 'Secador de Grãos', category: 'outros', icon: '🌡️' },
  { id: 'balanca-agricola', name: 'Balança Agrícola', category: 'outros', icon: '⚖️' },
  { id: 'gerador', name: 'Gerador', category: 'outros', icon: '⚡' },
  { id: 'drone-monitoramento', name: 'Drone de Monitoramento', category: 'outros', icon: '🚁' },
]

/**
 * AGC: Machine category info
 */
export interface MachineCategoryInfo {
  id: MachineCategory
  name: string
  icon: string
  iconImg?: string
}

/**
 * AGC: Machine categories list — transparent icons
 */
export const MACHINE_CATEGORIES: MachineCategoryInfo[] = [
  { id: 'trator', name: 'Tratores', icon: '🚜', iconImg: '/Machinery_icons/machinery_transparent/trator109.png' },
  { id: 'colheitadeira', name: 'Colheitadeiras', icon: '🌾', iconImg: '/Machinery_icons/machinery_transparent/colheitadeira09.png' },
  { id: 'plantadeira', name: 'Plantadeiras', icon: '🌱', iconImg: '/Machinery_icons/machinery_transparent/plantadeira56.png' },
  { id: 'pulverizador', name: 'Pulverizadores', icon: '💧', iconImg: '/Machinery_icons/machinery_transparent/pulverizador78.png' },
  { id: 'distribuidor', name: 'Distribuidores', icon: '🌾', iconImg: '/Machinery_icons/machinery_transparent/distribuidor37.png' },
  { id: 'implemento', name: 'Implementos', icon: '⚙️', iconImg: '/Machinery_icons/machinery_transparent/implemento50.png' },
  { id: 'irrigacao', name: 'Irrigação', icon: '💦', iconImg: '/Machinery_icons/machinery_transparent/irrigacao51.png' },
  { id: 'transporte', name: 'Transporte', icon: '🚛', iconImg: '/Machinery_icons/machinery_transparent/caminhao_graneleiro_verde06.png' },
  { id: 'outros', name: 'Outros', icon: '📦', iconImg: '/Machinery_icons/machinery_transparent/outros54.png' },
]

// AGC: Brand → icon color mapping (closest available icon color for dominant machine body color)
const BRAND_COLOR_MAP: Record<string, string> = {
  'john deere': 'verde',
  'massey ferguson': 'vermelho',
  'case ih': 'vermelho',
  'case': 'vermelho',
  'valtra': 'amarelo',
  'new holland': 'azul',
  'landini': 'azul',
  'fendt': 'verde',
  'kubota': 'laranja',
  'mahindra': 'vermelho',
  'agrale': 'vermelho',
  'claas': 'verde',
  'stara': 'laranja',
  'jacto': 'laranja',
  'montana': 'vermelho',
  'pla': 'amarelo',
  'uniport': 'laranja',
  'semeato': 'vermelho',
  'tatu marchesan': 'vermelho',
  'jumil': 'vermelho',
  'baldan': 'vermelho',
  'vence tudo': 'vermelho',
  'kuhn': 'vermelho',
  'piccin': 'verde',
  'imasa': 'azul',
  'civemasa': 'laranja',
  'kf implementos': 'verde',
  'jan': 'vermelho',
  'ikeda': 'azul',
  'valley': 'azul',
  'lindsay': 'azul',
  'reinke': 'azul',
  'fockink': 'verde',
  'mercedes-benz': 'preto',
  'volvo': 'azul',
  'scania': 'azul',
  'volkswagen': 'azul',
  'ford': 'azul',
  'iveco': 'azul',
  'randazzo': 'vermelho',
  'guerra': 'preto',
  'jf': 'vermelho',
  // AGC: Argentine brands — colors per references/maquinaria_agricola_AR.md (PT color IDs to match ICON_MAP).
  'pauny': 'amarelo',
  'zanello': 'verde',
  'metalfor': 'verde',
  'akron': 'verde',
  'bertini': 'verde',
  'vassalli': 'vermelho',
  'don roque': 'vermelho',
  'mainero': 'vermelho',
  'apache': 'vermelho',
  'crucianelli': 'vermelho',
  'richiger': 'vermelho',
  'ombu': 'vermelho',
  'ombú': 'vermelho',
  'agrometal': 'azul',
  'cestari': 'azul',
  'hanomag': 'azul',
  'same': 'azul',
  'yomel': 'azul',
  'montecor': 'verde',
  'helvetica': 'vermelho',
  'helvética': 'vermelho',
  'deutz-fahr': 'verde',
  'vw': 'azul',
  'mercedes': 'preto',
}

// AGC: Normalize English categories from legacy Firestore data to Portuguese
const CATEGORY_NORMALIZE: Record<string, string> = {
  tractor: 'trator', harvester: 'colheitadeira', sprayer: 'pulverizador',
  planter: 'plantadeira', spreader: 'distribuidor', implement: 'implemento',
  truck: 'transporte', drone: 'outros',
}

const B = '/Machinery_icons/machinery_transparent'

// AGC: Icon lookup — filenames have sequential numbers, can't be built dynamically
// AGC: Visual mapping (file names ≠ visual size):
// AGC:   trator_pequeno_*2* files = PEQUENO (open ROPS, no cabin)
// AGC:   trator_pequeno_* files   = MÉDIO (enclosed cabin, mid-size)
// AGC:   trator_medio_* files     = GRANDE (big cabin, large tires)
// AGC:   trator_articulado_*      = ARTICULADO (4WD, massive)
const ICON_MAP: Record<string, string> = {
  'trator_pequeno_amarelo': `${B}/trator_pequeno_amarelo2126.png`,
  'trator_pequeno_azul': `${B}/trator_pequeno_azul2128.png`,
  'trator_pequeno_laranja': `${B}/trator_pequeno_laranja2130.png`,
  'trator_pequeno_verde': `${B}/trator_pequeno_verde2132.png`,
  'trator_pequeno_vermelho': `${B}/trator_pequeno_vermelho2134.png`,
  'trator_medio_amarelo': `${B}/trator_pequeno_amarelo125.png`,
  'trator_medio_azul': `${B}/trator_pequeno_azul127.png`,
  'trator_medio_laranja': `${B}/trator_pequeno_laranja129.png`,
  'trator_medio_verde': `${B}/trator_pequeno_verde131.png`,
  'trator_medio_vermelho': `${B}/trator_pequeno_vermelho133.png`,
  'trator_grande_amarelo': `${B}/trator_medio_amarelo120.png`,
  'trator_grande_azul': `${B}/trator_medio_azul121.png`,
  'trator_grande_laranja': `${B}/trator_medio_laranja122.png`,
  'trator_grande_verde': `${B}/trator_medio_verde123.png`,
  'trator_grande_vermelho': `${B}/trator_medio_vermelho124.png`,
  'trator_articulado_amarelo': `${B}/trator_articulado_amarelo110.png`,
  'trator_articulado_azul': `${B}/trator_articulado_azul111.png`,
  'trator_articulado_laranja': `${B}/trator_articulado_laranja112.png`,
  'trator_articulado_verde': `${B}/trator_articulado_verde113.png`,
  'trator_articulado_vermelho': `${B}/trator_articulado_vermelho114.png`,
  'colheitadeira_graos_amarelo': `${B}/colheitadeira_graos_amarelo31.png`,
  'colheitadeira_graos_azul': `${B}/colheitadeira_graos_azul32.png`,
  'colheitadeira_graos_laranja': `${B}/colheitadeira_graos_laranja33.png`,
  'colheitadeira_graos_verde': `${B}/colheitadeira_graos_verde34.png`,
  'colheitadeira_graos_vermelho': `${B}/colheitadeira_graos_vermelho35.png`,
  'plantadeira_4_linhas_amarelo': `${B}/plantadeira_4_linhas_amarelo67.png`,
  'plantadeira_4_linhas_azul': `${B}/plantadeira_4_linhas_azul68.png`,
  'plantadeira_4_linhas_laranja': `${B}/plantadeira_4_linhas_laranja69.png`,
  'plantadeira_4_linhas_verde': `${B}/plantadeira_4_linhas_verde271.png`,
  'plantadeira_4_linhas_vermelho': `${B}/plantadeira_4_linhas_vermelho72.png`,
  'plantadeira_12_linhas_amarelo': `${B}/plantadeira_12_linhas_amarelo57.png`,
  'plantadeira_12_linhas_azul': `${B}/plantadeira_12_linhas_azul58.png`,
  'plantadeira_12_linhas_laranja': `${B}/plantadeira_12_linhas_laranja59.png`,
  'plantadeira_12_linhas_verde': `${B}/plantadeira_12_linhas_verde60.png`,
  'plantadeira_12_linhas_vermelho': `${B}/plantadeira_12_linhas_vermelho61.png`,
  'plantadeira_24_linhas_amarelo': `${B}/plantadeira_24_linhas_amarelo62.png`,
  'plantadeira_24_linhas_azul': `${B}/plantadeira_24_linhas_azul63.png`,
  'plantadeira_24_linhas_laranja': `${B}/plantadeira_24_linhas_laranja64.png`,
  'plantadeira_24_linhas_verde': `${B}/plantadeira_24_linhas_verde65.png`,
  'plantadeira_24_linhas_vermelho': `${B}/plantadeira_24_linhas_vermelho66.png`,
  'plantadeira_pneumatica_amarelo': `${B}/plantadeira_pneumatica_amarelo73.png`,
  'plantadeira_pneumatica_azul': `${B}/plantadeira_pneumatica_azul74.png`,
  'plantadeira_pneumatica_laranja': `${B}/plantadeira_pneumatica_laranja75.png`,
  'plantadeira_pneumatica_verde': `${B}/plantadeira_pneumatica_verde76.png`,
  'plantadeira_pneumatica_vermelho': `${B}/plantadeira_pneumatica_vermelho77.png`,
  'pulverizador_barras_amarelo': `${B}/pulverizador_barras_amarelo84.png`,
  'pulverizador_barras_azul': `${B}/pulverizador_barras_azul85.png`,
  'pulverizador_barras_laranja': `${B}/pulverizador_barras_laranja86.png`,
  'pulverizador_barras_verde': `${B}/pulverizador_barras_verde288.png`,
  'pulverizador_barras_vermelho': `${B}/pulverizador_barras_vermelho89.png`,
  'pulverizador_autopropelido_amarelo': `${B}/pulverizador_autopropelido_amarelo79.png`,
  'pulverizador_autopropelido_azul': `${B}/pulverizador_autopropelido_azul80.png`,
  'pulverizador_autopropelido_laranja': `${B}/pulverizador_autopropelido_laranja81.png`,
  'pulverizador_autopropelido_verde': `${B}/pulverizador_autopropelido_verde82.png`,
  'pulverizador_autopropelido_vermelho': `${B}/pulverizador_autopropelido_vermelho83.png`,
  'pulverizador_costal_amarelo': `${B}/pulverizador_costal_amarelo90.png`,
  'pulverizador_costal_azul': `${B}/pulverizador_costal_azul91.png`,
  'pulverizador_costal_laranja': `${B}/pulverizador_costal_laranja92.png`,
  'pulverizador_costal_verde': `${B}/pulverizador_costal_verde93.png`,
  'pulverizador_costal_vermelho': `${B}/pulverizador_costal_vermelho94.png`,
  'atomizador_amarelo': `${B}/atomizador_amarelo07.png`,
  'atomizador_azul': `${B}/atomizador_azul08.png`,
  'atomizador_laranja': `${B}/atomizador_laranja09.png`,
  'atomizador_verde': `${B}/atomizador_verde10.png`,
  'atomizador_vermelho': `${B}/atomizador_vermelho11.png`,
  'distribuidor_calcario_amarelo': `${B}/distribuidor_calcario_amarelo40.png`,
  'distribuidor_calcario_azul': `${B}/distribuidor_calcario_azul41.png`,
  'distribuidor_calcario_laranja': `${B}/distribuidor_calcario_laranja42.png`,
  'distribuidor_calcario_verde': `${B}/distribuidor_calcario_verde43.png`,
  'distribuidor_calcario_vermelho': `${B}/distribuidor_calcario_vermelho44.png`,
  'distribuidor_adubo_amarelo': `${B}/semeadora_lanco_amarelo97.png`,
  'distribuidor_adubo_azul': `${B}/semeadora_lanco_azul98.png`,
  'distribuidor_adubo_laranja': `${B}/semeadora_lanco_laranja99.png`,
  'distribuidor_adubo_verde': `${B}/semeadora_lanco_verde100.png`,
  'distribuidor_adubo_vermelho': `${B}/semeadora_lanco_vermelho101.png`,
  'semeadora_lanco_amarelo': `${B}/semeadora_lanco_amarelo97.png`,
  'semeadora_lanco_azul': `${B}/semeadora_lanco_azul98.png`,
  'semeadora_lanco_laranja': `${B}/semeadora_lanco_laranja99.png`,
  'semeadora_lanco_verde': `${B}/semeadora_lanco_verde100.png`,
  'semeadora_lanco_vermelho': `${B}/semeadora_lanco_vermelho101.png`,
  'caminhao_graneleiro_amarelo': `${B}/caminhao_graneleiro_amarelo01.png`,
  'caminhao_graneleiro_azul': `${B}/caminhao_graneleiro_azul02.png`,
  'caminhao_calcario_laranja': `${B}/caminhao_calcario_laranja03.png`,
  'caminhao_graneleiro_preto': `${B}/caminhao_graneleiro_preto04.png`,
  'caminhao_graneleiro_verde': `${B}/caminhao_graneleiro_verde06.png`,
  'caminhao_graneleiro_vermelho': `${B}/caminhao_graneleiro_vermelho07.png`,
}

// AGC: Default sub-type per category
const CATEGORY_DEFAULT_SUBTYPE: Record<string, string> = {
  trator: 'trator_medio',
  colheitadeira: 'colheitadeira_graos',
  plantadeira: 'plantadeira_12_linhas',
  pulverizador: 'pulverizador_barras',
  distribuidor: 'distribuidor_calcario',
  transporte: 'caminhao_graneleiro',
}

// AGC: Check if a brand has a known color mapping
export function isKnownBrand(brand: string): boolean {
  return brand ? brand.toLowerCase() in BRAND_COLOR_MAP : false
}

/**
 * AGC: Resolve the best transparent icon for a machine based on brand, category, and specs
 */
export function getMachineIcon(
  category: string,
  brand?: string,
  specs?: { numberOfLines?: number; horsePower?: number; hopperCapacityKg?: number },
  customIconPath?: string,
  machineName?: string,
  brandColor?: string,
): string {
  if (customIconPath) return customIconPath

  const cat = CATEGORY_NORMALIZE[category?.toLowerCase()] || category?.toLowerCase() || 'outros'
  const color = brandColor || (brand ? BRAND_COLOR_MAP[brand.toLowerCase()] || 'verde' : 'verde')
  const nameLower = machineName?.toLowerCase() || ''

  let subtypeKey: string | undefined

  // AGC: Trator — infer subtype from name keywords, then HP, then default
  if (cat === 'trator') {
    if (nameLower.includes('articulado')) subtypeKey = 'trator_articulado'
    else if (nameLower.includes('esteira')) subtypeKey = 'trator_esteira'
    else if (nameLower.includes('grande')) subtypeKey = 'trator_grande'
    else if (nameLower.includes('pequeno')) subtypeKey = 'trator_pequeno'
    else if (specs?.horsePower) {
      const hp = specs.horsePower
      subtypeKey = hp <= 75 ? 'trator_pequeno' : hp <= 180 ? 'trator_medio' : 'trator_grande'
    }
  }
  // AGC: Plantadeira — infer from name or specs
  else if (cat === 'plantadeira') {
    if (nameLower.includes('pneum')) subtypeKey = 'plantadeira_pneumatica'
    else if (nameLower.includes('lanço') || nameLower.includes('lanco') || nameLower.includes('semeadora')) subtypeKey = 'semeadora_lanco'
    else {
      const lines = specs?.numberOfLines || parseInt(nameLower.match(/(\d+)\s*linh/)?.[1] || '0')
      if (lines > 0) subtypeKey = `plantadeira_${lines <= 8 ? '4_linhas' : lines <= 18 ? '12_linhas' : '24_linhas'}`
    }
  }
  // AGC: Pulverizador — infer from name
  else if (cat === 'pulverizador') {
    if (nameLower.includes('autopropelido') || nameLower.includes('uniport')) subtypeKey = 'pulverizador_autopropelido'
    else if (nameLower.includes('costal')) subtypeKey = 'pulverizador_costal'
    else if (nameLower.includes('atomiz')) subtypeKey = 'atomizador'
  }
  // AGC: Distribuidor — by capacity: ≥5t = big trailer (calcário), <5t = small hopper (adubo)
  else if (cat === 'distribuidor') {
    if (nameLower.includes('adubadeira') && (nameLower.includes('linha') || nameLower.includes('cobertura'))) subtypeKey = 'adubadeira_linha'
    else {
      const capKg = specs?.hopperCapacityKg || 0
      const nameKg = parseInt((nameLower.match(/(\d+)\s*kg/) || [])[1] || '0')
      const nameTon = parseInt((nameLower.match(/(\d+)\s*t(?:on|n)?(?:\s|$)/) || [])[1] || '0')
      const effectiveKg = capKg || nameKg || (nameTon * 1000)
      if (effectiveKg >= 5000) subtypeKey = 'distribuidor_calcario'
      else if (effectiveKg > 0) subtypeKey = 'distribuidor_adubo'
    }
  }

  if (!subtypeKey) subtypeKey = CATEGORY_DEFAULT_SUBTYPE[cat]

  if (subtypeKey) {
    const icon = ICON_MAP[`${subtypeKey}_${color}`]
    if (icon) return icon
    const fallback = ICON_MAP[`${subtypeKey}_verde`]
    if (fallback) return fallback
  }

  const catInfo = MACHINE_CATEGORIES.find(c => c.id === cat)
  return catInfo?.iconImg || `${B}/outros54.png`
}

/**
 * AGC: Get machines by category
 */
export const getMachinesByCategory = (category: MachineCategory): FarmMachine[] => {
  return FARM_MACHINES.filter(m => m.category === category)
}

/**
 * AGC: Get machine by ID
 */
export const getMachineById = (id: string): FarmMachine | undefined => {
  return FARM_MACHINES.find(m => m.id === id)
}

/**
 * AGC: Get brands by category
 */
export const getBrandsByCategory = (category: MachineCategory): string[] => {
  return MACHINE_BRANDS[category] || []
}




